import { TabLines } from '@toggle/design-system';
import { AssetSubClassCode, Entity, SnakeMeta } from '@toggle/toggle';
import { startOfDay, subDays } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsEarningsInsightsEnabled } from '~/hooks/use-is-earnings-insights-enabled/useIsEarningsInsightsEnabled';
import { TabsType } from '~/modules/tab-content/types';
import { useCatalystInsightsList } from '~/react-query-hooks/insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '~/react-query-hooks/insights/useEarningsInsightsFilter';
import { parseEarningsInsights } from '~/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { useCompanyTheme } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { CatalystInsightPill } from '~/widgets/catalyst-insights-drawer/catalyst-insight-pill/CatalystInsightPill';
import { EarningsInsightPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';

import { EntityHeader } from '../entity-header/EntityHeader';
import { ChartPrice } from '../simple-chart/components/chart-price/ChartPrice';
import { getTabs } from '../tab-content/utils';
import * as S from './AssetOverviewHeader.styles';

export interface AssetOverviewHeaderProps {
  snakeMeta?: SnakeMeta;
  entity: Entity;
  activeTab: TabsType;
  changeTab: (index: number, tabKey: TabsType) => void;
  isCondensed: boolean;
  showShadow: boolean;
  documentsCount?: number;
}

export const AssetOverviewHeader = ({
  snakeMeta,
  entity,
  activeTab,
  changeTab,
  isCondensed,
  showShadow,
  documentsCount = 0,
}: AssetOverviewHeaderProps) => {
  const { t } = useTranslation([
    'chart',
    'analyze',
    'countries',
    'asset-overview',
    'earnings',
  ]);
  const isEarningsInsightsEnabled = useIsEarningsInsightsEnabled();
  const isCatalystInsightsEnabled = useFlags(state =>
    state.getFeatureFlagState('catalysts_company_insights')
  );
  const isStreamFeedEnabled = useFlags(state =>
    state.getFeatureFlagState('stream_feed')
  );
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const showStreamFeatures = isStreamFeedEnabled && !isBasicOrLightUser;

  const { error } = useCompanyTheme({ entityTag: entity.tag });

  const fromDate = useMemo(() => startOfDay(subDays(new Date(), 3)), []);
  const { data: catalystInsightsData } = useCatalystInsightsList(
    {
      entities: [entity.tag],
      categories: ['company'],
      from: fromDate.toISOString(),
      page: 1,
      page_size: 1,
    },
    isCatalystInsightsEnabled && !showStreamFeatures
  );
  const { data: earningsInsightsData = [] } = useEarningsInsightsFilter({
    body: {
      entities: [entity.tag],
      from: fromDate.toISOString(),
    },
    isEnabled: isEarningsInsightsEnabled,
  });
  const earningsInsight = parseEarningsInsights(earningsInsightsData);
  const isPreview = earningsInsight?.type === 'preview';

  const is404Error = error?.status === 404;
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const isEcoCentralBank =
    entity.sub_class === AssetSubClassCode.EcoCentralBank;
  const isDocumentsTabVisible =
    (isStock || isEcoCentralBank) && !!documentsCount;
  const tabs = getTabs(t, { isStock, isDocumentsTabVisible, is404Error });
  const filteredTabs = tabs.filter(tab => !tab.isHidden);

  return (
    <S.Header $showShadow={showShadow}>
      <S.Layout>
        <S.HeaderContent $hasEntity={!!entity} $isCondensed={isCondensed}>
          {entity && (
            <>
              <S.ContentWrapper $isCollapsed={isCondensed}>
                <EntityHeader
                  entity={entity}
                  rightNode={
                    <>
                      {!!earningsInsight && !showStreamFeatures && (
                        <EarningsInsightPill
                          entityTag={entity.tag}
                          earningsInsight={earningsInsight}
                          isPreview={isPreview}
                          source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                        />
                      )}
                      {!!catalystInsightsData?.length &&
                        !showStreamFeatures && (
                          <CatalystInsightPill
                            insightId={catalystInsightsData[0].id}
                            isClickable
                            entityTag={entity.tag}
                            source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                          />
                        )}
                    </>
                  }
                  priceDisplayNode={
                    <ChartPrice
                      entity={entity}
                      snakeMeta={snakeMeta}
                      isCondensed={isCondensed}
                      enablePriceHover={false}
                    />
                  }
                  isCondensed={isCondensed}
                />
              </S.ContentWrapper>
              <TabLines
                tabs={filteredTabs}
                activeTab={activeTab}
                onChange={changeTab}
              />
            </>
          )}
        </S.HeaderContent>
      </S.Layout>
    </S.Header>
  );
};
